import { useMemo } from "react";
import {
  useForm,
  useWatch,
  type Control,
  type UseFormReturn,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { POLICY_PRESETS, type AcquirePolicy } from "../utils/acquirePolicy";
import {
  bulkAcquireFormSchema,
  policyFromForm,
  fieldsFromPolicy,
  POLICY_FIELD_NAMES,
  type BulkAcquireFormData,
  type PolicyFields,
} from "../utils/bulkAcquireFormSchema";

/** フォームの取得条件フィールドを購読し、取得条件へ変換する。呼び出し側の再計算を値が変わったときだけに抑える */
export const usePolicyFromForm = (
  control: Control<BulkAcquireFormData>
): { fields: PolicyFields; policy: AcquirePolicy } => {
  const [useAcquired, useReuse, useNew, ignoreWindow] = useWatch({
    control,
    name: POLICY_FIELD_NAMES,
  });
  const fields = useMemo(
    () => ({ useAcquired, useReuse, useNew, ignoreWindow }),
    [useAcquired, useReuse, useNew, ignoreWindow]
  );
  const policy = useMemo(() => policyFromForm(fields), [fields]);
  return { fields, policy };
};

/** 一括取得画面のフォームインスタンスを生成する。既定は新方式のリユース優先＋未取得のみ（安く済む側に倒す） */
export const useBulkAcquireForm = (): UseFormReturn<BulkAcquireFormData> =>
  useForm<BulkAcquireFormData>({
    mode: "all",
    defaultValues: {
      identifyName: "",
      mode: "policy",
      ...fieldsFromPolicy(POLICY_PRESETS.REUSE_PRIORITY),
      pastDays: 0,
      excludeAcquired: true,
    },
    resolver: zodResolver(bulkAcquireFormSchema),
  });
